<template>
  <div>
    <div
      v-if="visibleAnswers.length > 0"
      class="space-y-6">
      <div
        v-for="answer in visibleAnswers"
        :key="answer.id"
        class="space-y-3">
        <span class="block text-sm font-medium text-text-primary">
          {{ answerLabel(answer) }}
        </span>
        <ColorPickerButton
          :ariaLabel="$t('W: Farve for {answer}', { answer: answerLabel(answer) })"
          :modelValue="modelValue[answer.id] ?? null"
          @update:model-value="onPick(answer.id, $event)" />
      </div>
    </div>
    <p
      v-else
      class="text-sm text-text-muted italic">
      {{ $t('W: Tilføj svar under Detaljer for at give dem en farve') }}
    </p>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapState } from 'pinia'
import type { IAnalysisTestAnswer, TResultColorId } from '@team-milva/lab-models/resources'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import { useAppStore } from '@/stores/appStore'

/**
 * One colour per answer of a single-select test (MIL-550) — the classification UI for that type,
 * shared by the edit pane's "Klassificering" tab, the focus view and the create-test wizard.
 * Laid out like the yes/no test's two outcomes: the answer's text above a colour picker that also
 * offers "no colour". There is no label setting; the answer's own text is what prints.
 *
 * Inactive answers are not listed — nobody can pick them any more — but their colour stays in the
 * model the parent holds, so an old order that used one still prints in the colour it had.
 */
export default {
  name: 'AnalysisTestAnswerColors',

  emits: ['update:modelValue'],

  props: {
    answers: {
      type: Array as PropType<IAnalysisTestAnswer[]>,
      required: true,
    },
    // Keyed by answer ID; absent or null means no colour.
    modelValue: {
      type: Object as PropType<Record<string, TResultColorId | null>>,
      required: true,
    },
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),

    visibleAnswers(): IAnalysisTestAnswer[] {
      return this.answers.filter((x) => x.activeState === 'active')
    },
  },

  methods: {
    answerLabel(answer: IAnalysisTestAnswer): string {
      return getTextObjValue(answer.text, this.currentLocale) || this.$t('W: (uden tekst)')
    },

    onPick(answerID: string, colorID: TResultColorId | null): void {
      this.$emit('update:modelValue', { ...this.modelValue, [answerID]: colorID })
    },
  },
}
</script>
